import { useState, useEffect } from "react";
import { difference, isEmpty, omit } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";

// Store
import {
    getShortList,
    fetchShortListGraphs,
    getShortListGraphsStatus,
    clearRefinementData,
    useLongListSlice,
} from "store/slice/refinement";
import {
    setLongList,
    addMemberLongList,
    removeMemberLongList,
    markAsNotInterest,
    getNotInterstedLongList,
    toggleSelectLongList,
    toggleCheckLongList,
    resetSelectedAndCheckedLongList,
} from "store/slice/refinement/longList";
import {
    setShortList,
    addMemberShortList,
    removeMemberShortList,
    toggleSelectedShortList,
    toggleCheckedShortList,
    resetSelectedAndCheckedShortList,
} from "store/slice/refinement/shortList";
import { getRolesThunk, getRoles, DraxRole } from "store/slice/Team/roles";
import { getLoaderState, toggleLoader } from "store/slice/UI";
import { setLongListWithIn } from "store/slice/refinement/longList/longListSlice";
import { getBoardStructureData } from "store/slice/Team/boardStructure/boardStructureSlice";
import { convertedOpenSearchData, setBoardStructure } from "store/slice/Team";
import { fetchPersonSet, updatePersonSet, getPersonSetByPath } from "store/slice/Project/peopleSet/peopleSetSlice";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import {
    getSearchAiById,
    fetchProjectSearchAi,
    updateSearchAiChild,
} from "store/slice/Project/searchAis/searchAisSlice";

// Hooks
import { useRedirectOnProjectError } from "utils";
import { useAppSelector, useAppDispatch } from "app/hooks";
import useGraphsFetch, { getTargetCompanyBoard } from "utils/hooks/useGraphsFetch";

// Utils
import { shallowEqual } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { convertOpenSearchToMember } from "store/slice/Team/boardStructure/boardStructure.functions";
import {
    getInitialFetchResponse,
    getInsightRelatedSltPath,
    getListsFetchResponse,
    getPersonMetaUI,
    getSearchAiLeadersLongList,
    getSearchAiLeadersShortList,
    TInitialFetch,
    TInitialFetchError,
} from "pages/ProjectDashboard/adapters/formattedData";

// Enums
import { urlPaths } from "enums/urlPaths";

// Type
import { Member } from "store/slice/Team/team.types";
import { EnumTeamStructure, TPersonInfo } from "components/Team/Team.types";
import { InsightData, ProjectPersonSet, ProjectSearchAICreationResponse } from "services/projects/project.types";
import {
    requestPeopleData,
    saveProjectByPathService,
    talentPotentialMembersPayloadColumns,
    updateProjectByPathService,
} from "services";

export type TuseRefinementSelectors = {
    shortList: Member[];
    longList: Member[];
    personInfo: TPersonInfo;
    toggleShowWorkHistory: boolean;
    filterLongListValue: string;
    rolesList: DraxRole[];
    notInterestedList: Member[];
    showError: boolean;
    showOnboardingText: boolean;
    selectedMembersHasRoles: boolean;
    filteredLongList: Member[];
    anchorEl: HTMLElement | null;
};

export type TuseRefinementHandlers = {
    setPersonInfo: React.Dispatch<React.SetStateAction<TPersonInfo>>;
    setToggleShowWorkHistory: React.Dispatch<React.SetStateAction<boolean>>;
    setFilterLongListValue: React.Dispatch<React.SetStateAction<string>>;
    onClose: () => void;
    removeActionMember: (member: Member) => void;
    addActionMember: (member: Member) => void;
    handleShowCharts: () => void;
    notIntestedLongList: (member: Member) => void;
    selectActionMember: (arg0: Member, arg1: string | undefined) => void;
    checkActionMember: (arg0: Member, arg1: string | undefined) => void;
    setShowError: (arg0: boolean) => void;
    handleShowOnboardingText: () => void;
    updateShortList: (arg0: Member[]) => void;
    updateLongList: (arg0: Member[]) => void;
    handleUpdateRefinement: () => Promise<void>;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
};

type TuseRefinement = () => [TuseRefinementSelectors, TuseRefinementHandlers];

const initialPersonInfo: TPersonInfo = {
    role: "",
    id: -1,
    info: undefined,
};

const useRefinement: TuseRefinement = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const searchAiId = searchParams.get("searchAiId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([projectId, insightId, searchAiId]);

    const isLoaderVisible = useAppSelector(getLoaderState);
    const { data: rolesData } = useAppSelector(getRoles);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);
    const { data: searchAiData } = useAppSelector(getSearchAiById(Number(searchAiId)), shallowEqual);

    const boardStructureList = useAppSelector(getBoardStructureData);
    const shortList = useAppSelector(getShortList);
    const { longList } = useLongListSlice();
    const notInterestedList = useAppSelector(getNotInterstedLongList);

    const shortListGraphStatus = useAppSelector(getShortListGraphsStatus);
    const params = omit(useGraphsFetch(["LD0", "LD4", "LD5", "LD8", "LD12"]), "peer_company_ids");
    const { data: roles } = useAppSelector(getRoles);

    const [rolesList, setRolesList] = useState(roles);
    const [personInfo, setPersonInfo] = useState(initialPersonInfo);
    const [toggleShowWorkHistory, setToggleShowWorkHistory] = useState(false);
    const [filterLongListValue, setFilterLongListValue] = useState("");
    const [showError, setShowError] = useState(true);
    const [showOnboardingText, setShowOnboardingText] = useState(true);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const [peopleLonglistPath, setPeopleLonglistPath] = useState("");
    const [peopleShortListPath, setPeopleShortListPath] = useState("");
    const [boardPath, setBoardPath] = useState("");

    const peopleLonglistData = useAppSelector(getPersonSetByPath(peopleLonglistPath), shallowEqual);
    const peopleShortlistData = useAppSelector(getPersonSetByPath(peopleShortListPath), shallowEqual);
    const boardData = useAppSelector(getPersonSetByPath(boardPath), shallowEqual);

    const filteredLongList =
        filterLongListValue === ""
            ? longList
            : longList.filter(({ name }) => name.toLowerCase().includes(filterLongListValue.toLowerCase()));

    const selectedMembersHasRoles = [...shortList, ...longList].some((member) => member.roleKey && member.isSelected);

    // Fetch data
    useEffect(() => {
        const initialFetch = async () => {
            const promises = [];
            let result = {} as TInitialFetch | TInitialFetchError;

            dispatch(toggleLoader(true));

            if (isEmpty(searchAiData) && projectId && searchAiId) {
                promises.push(
                    dispatch(
                        fetchProjectSearchAi({
                            projectId: Number(projectId),
                            path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}`,
                        })
                    )
                );
            }

            if (isEmpty(insightData) && projectId && insightId) {
                promises.push(
                    dispatch(
                        getProjectInsight({
                            projectId: Number(projectId),
                            path: `/project_${projectId}/insight_list/insight_${insightId}`,
                        })
                    )
                );
            }

            if (!rolesData.length) {
                promises.push(dispatch(getRolesThunk()));
            }

            if (promises.length && projectId) {
                const response = await Promise.allSettled(promises);
                result = getInitialFetchResponse(response);

                if ("error" in result) {
                    console.error(result.reason);
                    dispatch(toggleLoader(false));
                    navigate(urlPaths.NotFound);
                    return;
                }
            }

            const rolesParam = ("roles" in result ? result.roles : rolesData) ?? [];
            const searchAiParam = "searchAi" in result ? result.searchAi : searchAiData;
            const insightParam = "insight" in result ? result.insight : insightData;

            setRolesList(rolesParam);
            await setData(searchAiParam, insightParam, rolesParam);

            dispatch(toggleLoader(false));
        };

        const setData = async (
            searchAiData: ProjectSearchAICreationResponse | undefined,
            insightData: InsightData | undefined,
            roles: DraxRole[]
        ) => {
            if (!searchAiData || !insightData || projectId === null) return;

            // People Lists (ShortList and LongList)
            const promises = [];
            const longListSetPath = getSearchAiLeadersLongList(searchAiData)?.ref?.path;
            const shortListSetPath = getSearchAiLeadersShortList(searchAiData)?.ref?.path;

            if (longListSetPath) {
                setPeopleLonglistPath(longListSetPath);
                promises.push(dispatch(fetchPersonSet({ projectId, path: longListSetPath })));
            }

            if (shortListSetPath) {
                setPeopleShortListPath(shortListSetPath);
                promises.push(dispatch(fetchPersonSet({ projectId, path: shortListSetPath })));
            }

            const response = await Promise.allSettled(promises);
            const result = getListsFetchResponse<ProjectPersonSet>(response);

            if ("error" in result) {
                isLoaderVisible && dispatch(toggleLoader(false));
                console.error(result.reason);
                navigate(urlPaths.NotFound);
                return;
            }

            const [peopleSetLongList, peopleSetShortList] = result;

            // Shortlist
            const shortListSet = peopleSetShortList?.data.person_ids.map((id) => {
                const metaUI = getPersonMetaUI(peopleSetShortList, id);
                return { ...metaUI, id };
            });

            // LongList
            const longListSet = peopleSetLongList?.data.person_ids.map((id, i) => {
                const metaUI = getPersonMetaUI(peopleSetLongList, id);
                return { ...metaUI, id };
            });

            if (shortListSet?.length || longListSet?.length) {
                await fetchListsData(shortListSet, longListSet, insightData, roles);
            }
        };

        const fetchListsData = async (
            shortList: any[] | undefined,
            longList: any[] | undefined,
            insightData: InsightData | undefined,
            roles: DraxRole[]
        ) => {
            if (!insightData || projectId === null) return;

            const relatedSltPath = getInsightRelatedSltPath(insightData)?.ref?.path;

            if (!relatedSltPath) return;

            setBoardPath(relatedSltPath);

            const boardStructure: ProjectPersonSet = unwrapResult(
                await dispatch(fetchPersonSet({ projectId, path: relatedSltPath }))
            );

            if (boardStructure?.data.person_ids.length) {
                const boardStructureData = await requestPeopleData(
                    boardStructure?.data?.person_ids || [],
                    talentPotentialMembersPayloadColumns
                );

                const convertedBoardData = convertedOpenSearchData(boardStructureData);

                dispatch(
                    setBoardStructure(
                        convertOpenSearchToMember(convertedBoardData, boardStructure.meta_ui?.person_ids ?? [], roles)
                    )
                );
            }

            const result = await requestPeopleData(
                [...(shortList?.map((s) => s.id) || []), ...(longList?.map((s) => s.id) || [])],
                talentPotentialMembersPayloadColumns
            );

            const peopleWithData = convertedOpenSearchData(result);

            if (shortList) {
                dispatch(
                    setShortList(
                        peopleWithData
                            .filter((p: any) => shortList.map((e) => e.id).includes(Number(p.person_id)))
                            .map((member: any, i: number) => {
                                const memberMetaUI = shortList.find((e: any) => e.id === Number(member.person_id));
                                const isInBoard = boardStructure?.data.person_ids.find(
                                    (board) => board === Number(member.person_id)
                                );
                                return {
                                    ...member,
                                    person_id: Number(member.person_id),
                                    in: "shortList",
                                    statusUI: isInBoard ? ("disabled" as const) : ("enabled" as const),
                                    ...(memberMetaUI?.role_id
                                        ? {
                                              roleKey: `${memberMetaUI.role_id}`,
                                              role: roles.find((r) => r.id === Number(memberMetaUI.role_id))?.name,
                                          }
                                        : {}),
                                };
                            })
                    )
                );
            }

            if (longList) {
                dispatch(
                    setLongListWithIn(
                        peopleWithData
                            .filter((p: any) => longList.map((e) => e.id).includes(Number(p.person_id)))
                            .map((member: any, i: number) => {
                                const memberMetaUI = longList.find((e: any) => e.id === Number(member.person_id));
                                return {
                                    ...member,
                                    person_id: Number(member.person_id),
                                    in: memberMetaUI?.in,
                                    statusUI: "enabled" as const,
                                    ...(memberMetaUI?.role_id
                                        ? {
                                              roleKey: `${memberMetaUI.role_id}`,
                                              role: roles.find((r) => r.id === Number(memberMetaUI.role_id))?.name,
                                          }
                                        : {}),
                                };
                            })
                    )
                );
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check if there are member with no roles
    useEffect(() => {
        if (shortList.length || longList.length) {
            const selectedMembersWithNoRoles = [...shortList, ...longList].some(
                (member) => !member.roleKey && member.isSelected
            );
            setShowError(selectedMembersWithNoRoles);
        }
    }, [shortList, longList]);

    // If has charts remove the boarding text
    useEffect(() => {
        if (shortListGraphStatus === "done") {
            setShowOnboardingText(false);
        }
    }, [shortListGraphStatus]);

    const onClose = () => {
        setToggleShowWorkHistory((currentValue) => {
            if (currentValue) setPersonInfo(initialPersonInfo);

            return !currentValue;
        });
        setAnchorEl(null);
    };

    const handleShowOnboardingText = () => {
        setShowOnboardingText(!showOnboardingText);
    };

    const removeActionMember = (member: Member) => {
        dispatch(removeMemberShortList(member));
        dispatch(addMemberLongList({ member, position: "top" }));
    };

    const addActionMember = (member: Member) => {
        dispatch(removeMemberLongList(member));
        dispatch(addMemberShortList({ member, position: "top" }));
    };

    const notIntestedLongList = (member: Member) => {
        dispatch(markAsNotInterest(member));
    };

    const updateShortList = (updatedList: Member[], movedItem?: Member) => {
        dispatch(setShortList(updatedList));
    };

    const updateLongList = (updatedList: Member[], movedItem?: Member) => {
        const memberToRemove = difference(filteredLongList, updatedList)[0];

        if (memberToRemove) {
            const longListWithoutRemoved = longList.filter((member) => member.person_id !== memberToRemove.person_id);
            dispatch(setLongList(longListWithoutRemoved));
            return;
        }

        // TODO: refactor this. This is how items are moved if there is a filterable list or not.
        if (movedItem) {
            const movedItemIdx = updatedList.findIndex((el) => el.person_id === movedItem.person_id);
            const prevItem = updatedList[movedItemIdx - 1];
            const nextItem = updatedList[movedItemIdx + 1];

            let destIdx: number;
            const srcIdx = longList.findIndex((el) => el.person_id === movedItem.person_id);

            const fromAnotherList = srcIdx === -1;

            if (nextItem) {
                const nextElementInLongList = longList.find((el) => el.person_id === nextItem.person_id);
                const nextElementIdxInLongList = longList.findIndex(
                    (el) => el.person_id === nextElementInLongList?.person_id
                );

                destIdx =
                    srcIdx > nextElementIdxInLongList || fromAnotherList
                        ? nextElementIdxInLongList
                        : nextElementIdxInLongList - 1;
            } else if (prevItem) {
                const prevElementInLongList = longList.find((el) => el.person_id === prevItem.person_id);
                const prevElementIdxInLongList = longList.findIndex(
                    (el) => el.person_id === prevElementInLongList?.person_id
                );

                destIdx = !fromAnotherList ? prevElementIdxInLongList : prevElementIdxInLongList + 1;
            } else {
                destIdx = 0;
            }

            const updatedLongList = [...longList];

            !fromAnotherList && updatedLongList.splice(srcIdx, 1);
            updatedLongList.splice(destIdx, 0, movedItem);

            dispatch(setLongList(updatedLongList));
            return;
        }
    };

    const checkActionMember = (member: Member, type: string | undefined) => {
        if (type?.includes("shortlist")) dispatch(toggleCheckedShortList({ member: [member] }));
        if (type?.includes(EnumTeamStructure.LONG)) dispatch(toggleCheckLongList({ members: [member] }));
    };

    const selectActionMember = (member: Member, type: string | undefined) => {
        if (type?.includes("shortlist")) dispatch(toggleSelectedShortList(member));
        if (type?.includes(EnumTeamStructure.LONG)) dispatch(toggleSelectLongList([member]));
    };

    const handleShowCharts = async () => {
        try {
            dispatch(toggleLoader(true));

            // Save the lists before requesting the charts
            await updateProject(false);

            // get People with a role assign in both list
            const filterWithRoleAndSelected = (member: Member) => !!member.roleKey && member.isSelected;

            const members = [
                ...shortList.filter(filterWithRoleAndSelected),
                ...longList.filter(filterWithRoleAndSelected),
            ];

            if (members.length > 0) {
                await dispatch(
                    fetchShortListGraphs({ ...params, target_company_board: getTargetCompanyBoard(members) })
                );
            }
            dispatch(toggleLoader(false));
        } catch (error) {
            console.error(error);
            dispatch(toggleLoader(false));
        }
    };

    const updateProject = async (shouldShowLoader = true) => {
        try {
            shouldShowLoader && dispatch(toggleLoader(true));

            const longListRef = getSearchAiLeadersLongList(searchAiData)?.ref?.path as string;
            const shortListRef = getSearchAiLeadersShortList(searchAiData)?.ref?.path as string;
            const boardRef = getInsightRelatedSltPath(insightData)?.ref?.path;

            // Update LongList
            const payload = {
                data: {
                    person_ids: [...longList, ...notInterestedList].map((l) => Number(l.person_id)),
                },
                meta_ui: {
                    person_ids: [...longList, ...notInterestedList].map((l) => ({
                        id: Number(l.person_id),
                        ...(l.roleKey ? { role_id: Number(l.roleKey) } : {}),
                        ...(l.in ? { in: l.in } : {}),
                    })),
                },
            };

            if (longListRef) {
                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: longListRef,
                    data: payload,
                });

                if (peopleLonglistData) {
                    const updatedData = {
                        ...peopleLonglistData,
                        data: { ...peopleLonglistData.data, person_ids: payload.data.person_ids },
                        meta: { ...peopleLonglistData.meta, count: payload.data.person_ids.length },
                        meta_ui: payload.meta_ui,
                    };
                    dispatch(updatePersonSet(updatedData));
                }
            }

            if (shortListRef) {
                // Update Shortlist if exists
                const payload = {
                    data: {
                        person_ids: shortList.map((s) => Number(s.person_id)),
                    },
                    meta_ui: {
                        person_ids: shortList.map((s) => ({
                            id: Number(s.person_id),
                            role_id: Number(s.roleKey),
                            ...(s.statusUI ? { status_ui: s.statusUI } : {}),
                        })),
                    },
                };

                if (shortListRef) {
                    await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: shortListRef,
                        data: payload,
                    });

                    if (peopleShortlistData) {
                        const updatedData = {
                            ...peopleShortlistData,
                            data: { ...peopleShortlistData.data, person_ids: payload.data.person_ids },
                            meta: { ...peopleShortlistData.meta, count: payload.data.person_ids.length },
                            meta_ui: payload.meta_ui,
                        };
                        dispatch(updatePersonSet(updatedData));
                    }
                }
            } else {
                // Create Shortlist and update ref in SearchAI/People
                const { data } = await saveProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/person_set_list`,
                    data: {
                        data: {
                            name: `Short list ` + `${new Date().getTime()}`.slice(-5),
                            type: "SHORT_LIST",
                            person_ids: shortList.map((s) => Number(s.person_id)),
                        },
                        meta_ui: {
                            person_ids: shortList.map((s) => ({
                                id: Number(s.person_id),
                                role_id: Number(s.roleKey),
                            })),
                        },
                    },
                    echo: true,
                });

                const shortListPeopleSet = data.children.at(-1);
                const peopleSetId = shortListPeopleSet.data.id;

                if (!isEmpty(shortListPeopleSet)) {
                    dispatch(updatePersonSet(shortListPeopleSet));
                }

                // Update ref on corresponding SearchAI
                const payload = {
                    data: {},
                    ref: {
                        path: `/project_${projectId}/person_set_list/person_set_${peopleSetId}`,
                    },
                };

                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}/short_list`,
                    data: payload,
                });

                const shortListPeopleSetRef = getSearchAiLeadersShortList(searchAiData);

                if (shortListPeopleSetRef && searchAiId) {
                    const updatedData = {
                        ...shortListPeopleSetRef,
                        ref: payload.ref,
                    };

                    dispatch(
                        updateSearchAiChild({
                            data: updatedData,
                            id: Number(searchAiId),
                            type: "PROJECT_PERSON_SET_REF_B",
                        })
                    );
                }
            }

            // Update related board
            if (boardRef) {
                const payload = {
                    data: {
                        person_ids: boardStructureList.map((m) => Number(m.person_id)),
                    },
                    meta_ui: {
                        person_ids: boardStructureList.map((m) => ({
                            id: Number(m.person_id),
                            role_id: Number(m.roleKey),
                            ...(m.in ? { in: m.in } : {}),
                            ...(m.meta ? { meta: m.meta } : {}),
                        })),
                    },
                };

                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: boardRef,
                    data: payload,
                });

                if (boardData) {
                    const updatedData = {
                        ...boardData,
                        meta: { ...boardData.meta, count: payload.data.person_ids.length },
                        data: { ...boardData.data, person_ids: payload.data.person_ids },
                        meta_ui: payload.meta_ui,
                    };
                    dispatch(updatePersonSet(updatedData));
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            shouldShowLoader && dispatch(toggleLoader(false));
        }
    };

    const handleUpdateRefinement = async () => {
        try {
            dispatch(toggleLoader(true));
            await updateProject();
            await dispatch(resetSelectedAndCheckedLongList());
            await dispatch(resetSelectedAndCheckedShortList());
            await dispatch(clearRefinementData());
            dispatch(toggleLoader(false));
        } catch (error) {
            console.error(error);
            dispatch(toggleLoader(false));
        }
    };

    return [
        {
            shortList,
            longList,
            personInfo,
            toggleShowWorkHistory,
            filterLongListValue,
            rolesList,
            notInterestedList,
            showError,
            showOnboardingText,
            selectedMembersHasRoles,
            filteredLongList,
            anchorEl,
        },
        {
            setPersonInfo,
            setToggleShowWorkHistory,
            onClose,
            removeActionMember,
            addActionMember,
            setFilterLongListValue,
            handleShowCharts,
            notIntestedLongList,
            selectActionMember,
            checkActionMember,
            setShowError,
            handleShowOnboardingText,
            updateShortList,
            updateLongList,
            handleUpdateRefinement,
            setAnchorEl,
        },
    ];
};

export default useRefinement;
